import React, { useState, useEffect, useContext } from "react";
import { AppContext } from "../../AppContextProvider";
import { twinModalParams } from "../../data/SampleData";

const CUBE_CONFIGS = [
  {
    cid: 0,
    name: 'Everything',
    colors: { light: '#8DDBEA', mid: '#1F97AF', dark: '#114954' },
    gradientId: 'cube_everything'
  },
  {
    cid: 1,
    name: 'Identity',
    colors: { light: '#FF89D0', mid: '#B31388', dark: '#770059' },
    gradientId: 'cube_identity'
  },
  {
    cid: 2,
    name: 'Health',
    colors: { light: '#FF89D0', mid: '#B31388', dark: '#770059' },
    gradientId: 'cube_health'
  },
  {
    cid: 3,
    name: 'Social',
    colors: { light: '#00E49C', mid: '#007B52', dark: '#004B30' },
    gradientId: 'cube_social'
  },
  {
    cid: 4,
    name: 'Location',
    colors: { light: '#00E49C', mid: '#00B377', dark: '#006644' },
    gradientId: 'cube_location'
  },
  {
    cid: 5,
    name: 'Finance',
    colors: { light: '#A8A9FF', mid: '#4C4BDE', dark: '#2318BB' },
    gradientId: 'cube_finance'
  },
  {
    cid: 6,
    name: 'Career',
    colors: { light: '#71D1FF', mid: '#0095CC', dark: '#00405D' },
    gradientId: 'cube_career'
  },
  {
    cid: 7,
    name: 'Communication',
    colors: { light: '#E9B8FF', mid: '#9B4BDE', dark: '#63008E' },
    gradientId: 'cube_communication'
  },
  {
    cid: 8,
    name: 'Devices',
    colors: { light: '#C0C0C0', mid: '#808080', dark: '#404040' },
    gradientId: 'cube_devices'
  }
];

const CubeSVG = ({ config, size = 36 }) => {
  const { colors, gradientId } = config;
  const uniqueId = `${gradientId}_${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20.0007 36.6666V19.9999L36.6673 11.6666V28.3333L20.0007 36.6666Z"
        fill={`url(#${uniqueId}_0)`}
      />
      <path
        d="M3.33398 28.3333V11.6666L20.0007 19.9999V36.6666L3.33398 28.3333Z"
        fill={`url(#${uniqueId}_1)`}
      />
      <path
        d="M20.0007 19.9999L3.33398 11.6666L20.0007 3.33325L36.6673 11.6666L20.0007 19.9999Z"
        fill={`url(#${uniqueId}_2)`}
      />
      <defs>
        <linearGradient id={`${uniqueId}_0`} x1="24" y1="28" x2="30" y2="8" gradientUnits="userSpaceOnUse">
          <stop stopColor={colors.mid} />
          <stop offset="1" stopColor={colors.light} />
        </linearGradient>
        <linearGradient id={`${uniqueId}_1`} x1="3" y1="26" x2="25" y2="8" gradientUnits="userSpaceOnUse">
          <stop stopColor={colors.light} />
          <stop offset="1" stopColor={colors.dark} />
        </linearGradient>
        <linearGradient id={`${uniqueId}_2`} x1="31" y1="13" x2="8" y2="11" gradientUnits="userSpaceOnUse">
          <stop stopColor={colors.dark} />
          <stop offset="1" stopColor={colors.mid} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const CategoryCube = ({ config, progress, isNetworkViewMode }) => {
  const progressColor = config.colors.light;
  
  return (
    <div 
      className={`cube-category-item ${isNetworkViewMode ? 'non-clickable' : ''}`}
    >
      <div className="cube-wrapper">
        <CubeSVG config={config} size={36} />
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill" 
            style={{ 
              width: `${Math.min(progress, 100)}%`,
              backgroundColor: progressColor
            }} 
          />
        </div>
      </div>
      <div className="category-pill" style={{ borderColor: config.colors.light }}>
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.62794 6.5625V3.0625C2.62794 2.82237 2.82281 2.625 3.06544 2.625H6.56544C6.68045 2.625 6.79069 2.67052 6.87477 2.75317L11.2498 7.12817C11.4219 7.30027 11.4219 7.5747 11.2498 7.7468L7.74977 11.2468C7.57767 11.4189 7.30324 11.4189 7.13114 11.2468L2.75614 6.87179C2.67349 6.78914 2.62794 6.67843 2.62794 6.5625Z"
            fill={config.colors.light}
          />
        </svg>
        <span style={{ color: config.colors.light }}>{config.name}</span>
      </div>
    </div>
  );
};

export default function CubeCategories(props) {
  const context = useContext(AppContext);
  const [isDark, setIsDark] = useState(true);
  const isNetworkViewMode = props.isNetworkViewMode || false;

  useEffect(() => {
    if (
      document.documentElement.getAttribute("data-color-mode") === "dark" ||
      document.documentElement.getAttribute("data-color-mode") === null
    ) {
      setIsDark(true);
    } else {
      setIsDark(false);
    }
  }, [context.getState()?.isRender]);

  const getProgress = (cid) => {
    const catStats = twinModalParams.categories?.[cid];
    if (catStats) {
      return Math.round((catStats.usedData / catStats.maxData) * 100);
    }
    return 0;
  };

  const handleCategoryClick = (config) => {
    if (isNetworkViewMode) return;
    if (!props.isVisibility2) return;
    
    props.handleWindowInfoSizeChange();
    
    if (config.cid === 0) {
      props.setActiveTab("everything");
      sessionStorage.setItem("activeTab", JSON.stringify("everything"));
      props.setBubbleFilter(0, "everything");
      props.setCategory("everything");
    } else {
      props.setActiveTab(config.name);
      sessionStorage.setItem("activeTab", JSON.stringify(config.name));
      props.setBubbleFilter(config.cid, config.name);
      props.setCategory(config.name);
    }
  };

  const filteredConfigs = isNetworkViewMode 
    ? CUBE_CONFIGS.filter(c => c.cid !== 0)
    : CUBE_CONFIGS;

  return (
    <div className="cube-categories-container">
      {filteredConfigs.map((config) => (
        <CategoryCube
          key={config.cid}
          config={config}
          progress={getProgress(config.cid)}
          isNetworkViewMode={isNetworkViewMode}
        />
      ))}
    </div>
  );
}
